//Rounded PI
const PI = Math.round(100 * Math.PI) / 100;

//General Utility Functions
const findRectangle = (diameter, side) => {
  const radius = diameter / 2;

  return ((PI * Math.pow(radius, 2)) / side).toFixed(3);
};

const findDiameter = (length, width) => {
  return (Math.sqrt((width * length) / PI) * 2).toFixed(3);
};

const setExponent = (type) => {
  if (type === 'flex') return 0.398;
  if (type === 'metal') return 0.38;
  return 0;
};

/* 
  @params
  ductLength = length (ft)
  n = # of 90 degree pends
  f = # of 45 degree pends
  o = # of 180 degree pends
*/
const calcDuctBend = (ductLength, numOfBends = { n: 0, f: 0, o: 0 }) => {
  const { n, f, o } = numOfBends;
  return parseInt(ductLength) + n * 10 + f * 5 + o * 30;
};

/**
 * flex duct friction loss lookup table
 * keys: duct diameter
 * values: set multipliers
 */
const flexFrictionLossLookup = {
  3: 0.0004,
  4: 0.0001,
  5: 0.00003,
  6: 0.00001,
  7: 0.000006,
  8: 0.000003,
  9: 0.000002,
  10: 0.000001,
  12: 0.0000004,
  14: 0.0000002,
  16: 0.0000001,
  18: 0.00000005,
  20: 0.00000003,
  22: -0.00007524715385,
};

//CFM - given duct size (rectangle or circle), ductLength, frictionRate, and type
const calcCFM1 = (
  shape = { round: '', diameter: '', length: '', width: '' },
  frictionRate,
  ductLength,
  type,
) => {
  const exponent = setExponent(type);
  const diameter = shape.round ? shape.diameter : findDiameter(shape.length, shape.width);

  const cfm =
    Math.pow(Math.E, Math.log(diameter) / exponent) *
    (3.12 * Math.pow((frictionRate / ductLength) * 100, 0.5));

  return Math.round(cfm);
};

//CFM - given duct size (rectangle or circle), velocity
const calcCFM2 = (velocity, shape = { round: '', diameter: '', length: '', width: '' }) => {
  const diameter = shape.round ? shape.diameter : findDiameter(shape.length, shape.width);
  const cfm = velocity * (3.14 * Math.pow(diameter / 24, 2));
  return Math.round(cfm);
};

//Diameter - given frictionRate, cfm, ductLength, and type
const calcDiameter = (cfm, frictionRate, ductLength, type, numOfBends = { n: 0, o: 0, f: 0 }) => {
  const exponent = setExponent(type);
  const diameter = Math.pow(
    cfm / (3.12 * Math.pow((frictionRate / calcDuctBend(ductLength, numOfBends)) * 100, 0.5)),
    exponent,
  );
  return Math.round(10 * diameter) / 10;
};

//Velocity (FPM) - cfm, frictionRate, ductLength
const calcVelocity = (cfm, diameter) => {
  const velocity = cfm / (Math.pow(diameter / 24, 2) * PI);
  return Math.round(velocity);
};

/**
 * Friction Loss - cfm, ductLength, type, shape, numOfBends
 * metal is per 100' of duct
 * flex duct is per 20' of duct
 */
const calcFrictionLoss = (
  cfm,
  ductLength,
  type,
  shape = { round: '', diameter: '', length: '', width: '' },
  numOfBends = { n: 0, o: 0, f: 0 },
) => {
  const ductBend = calcDuctBend(ductLength, numOfBends) / 100;
  const diameter = shape.round ? shape.diameter : findDiameter(shape.length, shape.width);
  let multiplier = null;
  if (type === 'metal') {
    multiplier = Math.pow(cfm / (3.12 * Math.pow(diameter, 2.63)), 2);
  }
  if (type === 'flex') {
    const lookupValue =
      Object.keys(flexFrictionLossLookup).find((length) => diameter <= length) ||
      flexFrictionLossLookup[22];
    multiplier = flexFrictionLossLookup[lookupValue] * Math.pow(cfm, 2);
  }
  const result = multiplier * ductBend;
  return Math.round(1000 * result) / 1000;
};

const HvacDuctCalculation = (values) => {
  const round = values.shape === 'round';
  const numOfBends = { n: values.n, o: values.o, f: values.f };
  const dimensions = {
    round: round,
    diameter: values.diameter,
    length: values.length,
    width: values.width,
  };

  if (values.sizeBy === 'friction rate') {
    //friction rate
    if (values.ductSizeOrCfm === 'ductSize') {
      //inputs: frictionRate && ductSize
      //calc: cfm && velocity
      const cfm = calcCFM1(dimensions, values.frictionRate, values.ductLength, values.ductType);
      const velocity = round
        ? calcVelocity(cfm, values.diameter)
        : calcVelocity(cfm, findDiameter(values.length, values.width));
      return { cfm, velocity };
    } else {
      //inputs: frictionRate && cfm
      //calc: diameter && velocity
      const diameter = calcDiameter(
        values.cfm,
        values.frictionRate,
        values.ductLength,
        values.ductType,
        numOfBends,
      );
      const velocity = calcVelocity(values.cfm, diameter);
      return { diameter, velocity };
    }
  } else {
    //velocity

    //inputs: velocity && ductsize
    //calc: cfm && frictionRate
    if (values.ductSizeOrCfm === 'ductSize') {
      const cfm = calcCFM2(values.velocity, dimensions);
      const frictionRate = calcFrictionLoss(
        cfm,
        values.ductLength,
        values.ductType,
        dimensions,
        numOfBends,
      );
      return { cfm, frictionRate };
    } else {
      //inputs: velocity && cfm
      //calc: diameter && friction rate
      const frictionRate = calcFrictionLoss(
        values.cfm,
        values.ductLength,
        values.ductType,
        dimensions,
        numOfBends,
      );
      const diameter = calcDiameter(
        values.cfm,
        frictionRate,
        values.ductLength,
        values.ductType,
        numOfBends,
      );
      return { diameter, frictionRate };
    }
  }
};

export {
  calcCFM1,
  calcCFM2,
  calcDiameter,
  calcVelocity,
  findRectangle,
  findDiameter,
  setExponent,
  calcDuctBend,
  calcFrictionLoss,
  HvacDuctCalculation,
};
